import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { ScrollStoreCtx } from '@/stores/ScrollStore';
import { EN_APP_DEVICE } from '@/utils/functions/device';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { CSSProperties, useContext } from 'react';

import styles from './NavigationBar.module.scss';

export interface INavigationBarProps {
  buttonSlot?: 1 | 2;
  children: {
    center?: JSX.Element | null;
    left?: JSX.Element | null;
    right?: JSX.Element | null;
  };
  className?: string;
  style?: CSSProperties;
}

interface INavigationBarBodyProps {
  className?: string;
  collapsedClassName?: string;
  style?: CSSProperties;
}

interface IShadowProps {
  className?: string;
}

type NavigationBarComponent = React.FC<INavigationBarProps> & {
  Body: React.FC<INavigationBarBodyProps>;
  Bottom: React.FC;
  BottomShadow: React.FC;
  Shadow: React.FC<IShadowProps>;
};

export const NAVIGATION_BAR_HEIGHT = 48;

const NavigationBar: NavigationBarComponent = ({
  buttonSlot = 1,
  children: { center, left, right },
  className,
  style,
}) => {
  const globalStore = useContext(GlobalStoreCtx);
  const isAndroid = globalStore.AppDevice === EN_APP_DEVICE.ANDROID;
  const isDoubleButtons = buttonSlot === 2;

  // Android -> Android
  // iOS -> iOS
  // 기본값 -> iOS
  return (
    <nav className={clsx(className, styles.container)} style={style}>
      <div className={styles.wrap}>
        {(left || !isAndroid) && (
          <div
            className={clsx(
              isAndroid ? styles.leftWrapAndroid : styles.leftWrap,
              !isAndroid && isDoubleButtons && styles.doubleWrap,
            )}
          >
            {left}
          </div>
        )}
        <div className={styles.center}>{center}</div>
        {(right || !isAndroid) && (
          <div
            className={clsx(
              isAndroid ? styles.rightWrapAndroid : styles.rightWrap,
              isDoubleButtons && styles.doubleWrap,
            )}
          >
            {right}
          </div>
        )}
      </div>
    </nav>
  );
};

NavigationBar.Body = observer(({ collapsedClassName, children, className, style }) => {
  const scrollStore = useContext(ScrollStoreCtx);

  return (
    <div className={clsx(styles.body, scrollStore.IsGoingDown && collapsedClassName, className)} style={style}>
      {children}
    </div>
  );
});

// 하단 라인
NavigationBar.Bottom = observer(() => {
  const scrollStore = useContext(ScrollStoreCtx);

  return <div className={clsx(styles.bottom, scrollStore.IsGoingDown && styles.collapsedBottom)} />;
});

// 하단 라인 + 스크롤 그림자
NavigationBar.BottomShadow = observer(() => {
  const scrollStore = useContext(ScrollStoreCtx);

  return <div className={clsx(scrollStore.position.y > 0 ? styles.shadow : styles.bottom)} />;
});

// 스크롤 그림자
NavigationBar.Shadow = observer(({ className }) => {
  const scrollStore = useContext(ScrollStoreCtx);

  return <div className={clsx(scrollStore.position.y > 0 ? styles.shadow : undefined, className)} />;
});

export default NavigationBar;
